import Box from '@mui/material/Box'
import {
  Button,
  FormControl,
  TextField,
  Typography,
  FormLabel,
  TextFieldProps,
  Link,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import {
  signIn,
  confirmSignIn,
  SignInOutput,
  resetPassword,
  ResetPasswordOutput,
  confirmResetPassword,
} from 'aws-amplify/auth'
import { Amplify } from 'aws-amplify'
import { getUserId, updateTokensToStorage } from '../../utils/AuthUtil'
import { sessionStorage } from 'aws-amplify/utils'
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito'
import { setAuthenticated } from '../../actions/auth'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import DisplayErrorPopup from '../DisplayError'
import { showErrorMessage } from '../../actions/organization'
import { fetchUserOrganizationAsync } from '../../services/usersService'
import { fetchUserRolesAsync } from '../../services/accessControlService'
import { fetchOrganizationSubscriptionAsync } from '../../services/subscriptionService'
import { useNavigate } from 'react-router-dom'
import { OrganizationType } from '../../models/Organization'

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || '',
      userPoolClientId: process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID || '',
    },
  },
})
cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionStorage)

function Authenticator(props: any) {
  const dispatch = useDispatch<any>()
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.authenticated,
  )
  const selectedOrganization = useSelector(
    (state: RootState) => state.organization.selectedOrganization,
  )
  const currentSubscription = useSelector(
    (state: RootState) => state.payment.currentSubscriptionPlan,
  )

  useEffect(() => {
    if (isAuthenticated && !selectedOrganization) {
      dispatch(fetchUserOrganizationAsync({ userId: getUserId()! }))
      dispatch(fetchUserRolesAsync())
    }
    if (
      selectedOrganization &&
      selectedOrganization.type === OrganizationType.ARCHITECT
    )
      dispatch(
        fetchOrganizationSubscriptionAsync({
          organization_id: selectedOrganization?.id,
        }),
      )
  }, [isAuthenticated, selectedOrganization])

  return !isAuthenticated ? <Signin /> : <Renderchild child={props.children} />
}

export function Renderchild({ child }: any) {
  const navigate = useNavigate()
  const isSubscriptionNotValid = () => {
    return (
      currentSubscription &&
      (currentSubscription.error || currentSubscription.isExpired)
    )
  }

  const isSubscriptionPage = () => {
    const pathname = window.location.pathname
    return pathname == '/subscriptions'
  }

  const currentSubscription = useSelector(
    (state: RootState) => state.payment.currentSubscriptionPlan,
  )
  return (
    <>
      {!isSubscriptionPage() && isSubscriptionNotValid() ? (
        <Box
          sx={{
            '& > :not(style)': { m: 1 },
            m: 4,
          }}
          display='flex'
          flexDirection={'column'}
          justifyContent='center'
          alignItems='center'
          alignContent='center'
        >
          {currentSubscription?.error ||
            `Your Current Plan ${currentSubscription?.plan_name} is Expired`}
          <Button onClick={() => navigate(`/subscriptions`)}>
            {'Upgrade Now'}
          </Button>
        </Box>
      ) : (
        child
      )}
    </>
  )
}

export function Signin() {
  const dispatch = useDispatch<any>()
  const userNameRef = useRef<TextFieldProps>(null)
  const passwordRef = useRef<TextFieldProps>(null)
  const mfaCodeRef = useRef<TextFieldProps>(null)
  const newPasswordRef = useRef<TextFieldProps>(null)
  const confirmPasswordRef = useRef<TextFieldProps>(null)
  const phoneNumberRef = useRef<TextFieldProps>(null)
  const forgotPasswordUsernameRef = useRef<TextFieldProps>(null)
  const resetPasswordCodeRef = useRef<TextFieldProps>(null)
  const [userName, setUserName] = useState<string>()
  const [login, setLogin] = useState(false)
  const [mfa, setMfa] = useState(false)
  const [confirmPassword, setConfirmPassword] = useState(false)
  const [confirmForgotPassword, setConfirmForgotPassword] = useState(false)
  const [forgotPassword, setForgotPassword] = useState(false)

  var errors = {
    uppercase: {
      regex: /[A-Z]/,
      description: 'At least 1 uppercase letter',
      color: 'red',
    },
    lowercase: {
      regex: /[a-z]/,
      description: 'At least 1 lowercase letter',
      color: 'red',
    },
    digit: {
      regex: /[0-9]/,
      description: 'At least 1 numerical character (0-9)',
      color: 'red',
    },
    special: {
      regex: /[^A-Za-z0-9]/,
      description: 'At least 1 special character',
      color: 'red',
    },
    length: {
      regex: /^([a-zA-Z0-9_-]).{8,}$/,
      description: 'Minimum of 8 characters',
      color: 'red',
    },
  }

  const validatePassword = (value: string) => {
    return Object.entries(errors).flatMap(([name, { regex, description }]) => {
      const isValid = regex.test(value)
      return isValid
        ? { description, name, color: '#4141ed' }
        : { description, name, color: 'red' }
    })
  }

  const handleLogin = async () => {
    const userName = userNameRef.current?.value?.toString()
    const password = passwordRef.current?.value?.toString()

    if (!(userName?.length && userName?.length > 0)) {
      dispatch(showErrorMessage('Username is required'))
      return
    }
    if (!(password?.length && password?.length > 0)) {
      dispatch(showErrorMessage('Password is required'))
      return
    }
    try {
      const signinResponse = await signIn({
        username: userName || '',
        password: password || '',
      })
      await nextStep(signinResponse)
    } catch (error: any) {
      dispatch(showErrorMessage(error.message))
    }
  }

  const handleMFA = async () => {
    const mfaCode = mfaCodeRef.current?.value?.toString()
    const signinResponse = await confirmSignIn({
      challengeResponse: mfaCode || '',
    })
    await nextStep(signinResponse)
  }

  const handleResetPassword = async () => {
    const username = forgotPasswordUsernameRef.current?.value?.toString()!
    if (!(username?.length && username?.length > 0)) {
      dispatch(showErrorMessage('username is required'))
      return
    }
    const signinResponse = await resetPassword({ username })
    setUserName(username)
    handleResetPasswordNextSteps(signinResponse)
  }

  const handleResetPasswordNextSteps = (output: ResetPasswordOutput) => {
    const { nextStep } = output
    switch (nextStep.resetPasswordStep) {
      case 'CONFIRM_RESET_PASSWORD_WITH_CODE':
        const codeDeliveryDetails = nextStep.codeDeliveryDetails
        console.log(
          `Confirmation code was sent to ${codeDeliveryDetails.deliveryMedium}`,
        )
        setForgotPassword(false)
        setConfirmForgotPassword(true)
        break
      case 'DONE':
        console.log('Successfully reset password.')
        break
    }
  }

  const nextStep = async (signinResponse: SignInOutput) => {
    if (
      signinResponse?.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE'
    ) {
      setMfa(true)
    } else if (
      signinResponse?.nextStep?.signInStep ===
      'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
    ) {
      setConfirmPassword(true)
    } else if (signinResponse?.nextStep?.signInStep === 'DONE') {
      dispatch(setAuthenticated(true))
      await updateTokensToStorage()
      dispatch(fetchUserOrganizationAsync({ userId: getUserId()! }))
    }
  }

  const Header = () => {
    return (
      <div style={{ minHeight: '25vh', alignContent: 'center' }}>
        <img alt='Spacial logo' src='/images/logo_white_bg.png'></img>
        <DisplayErrorPopup />
      </div>
    )
  }

  const Footer = () => {
    return (
      <Typography
        variant='body2'
        align='center'
        fontSize={12}
        style={{ minHeight: '20vh', alignContent: 'center' }}
      >
        © Spacial {new Date().getFullYear()}
      </Typography>
    )
  }

  const LoginForm = () => {
    return (
      <Box
        component='form'
        sx={{
          '& > :not(style)': { m: 1 },
          background: '#06004A',
          color: 'white',
          backgroundImage: "url('/images/login_background.png')",
        }}
        display='flex'
        flexDirection={'column'}
        justifyContent='center'
        alignItems='center'
        alignContent='center'
      >
        <Header></Header>
        <FormControl sx={{ m: 1, minHeight: '45vh' }}>
          <FormLabel
            sx={{
              color: 'white',
              fontSize: '10px',
              margin: '3px',
              marginLeft: '15px',
            }}
          >
            Login
          </FormLabel>
          <TextField
            inputRef={userNameRef}
            placeholder='Email or Username'
            sx={{
              background: 'white',
              margin: '3px',
              width: '350px',
              opacity: '0px',
              borderRadius: '4px',
              padding: '8px, 8px, 8px, 16px',
            }}
            size='small'
            required={true}
          />
          <FormLabel
            style={{
              color: 'white',
              margin: '3px',
              marginLeft: '15px',
              fontSize: '10px',
            }}
          >
            Password
          </FormLabel>
          <TextField
            placeholder='Enter password'
            inputRef={passwordRef}
            type='password'
            sx={{
              background: 'white',
              margin: '3px',
              width: '350px',
              opacity: '0px',
              borderRadius: '4px',
            }}
            size='small'
            required={true}
          />
          <Link
            onClick={() => setForgotPassword(true)}
            sx={{ mt: 1, ml: 2, cursor: 'pointer' }}
          >
            Forgot your password?
          </Link>
          <Button onClick={handleLogin} sx={{ color: 'white', margin: '15px' }}>
            Sign in
          </Button>
        </FormControl>
        <Footer></Footer>
      </Box>
    )
  }

  const ConfirmPasswordForm = () => {
    const [newPassword, setNewPassword] = useState<string>()
    const [confirmNewPassword, setConfirmNewPassword] = useState<string>()
    const [phoneNumber, setPhoneNumber] = useState<string>('')
    const [passwordValidationErrorMessage, setPasswordValidationErrorMessage] =
      useState<any>(Object.values(errors))
    const [
      confirmPasswordValidationErrorMessage,
      setConfirmPasswordValidationErrorMessage,
    ] = useState<string>('')

    const handlePasswordChange = (value: string) => {
      setNewPassword(value)
      setPasswordValidationErrorMessage(validatePassword(value))
    }

    const handleConfirmPasswordChange = (value: string) => {
      if (newPassword !== value)
        setConfirmPasswordValidationErrorMessage("Password doesn't matches")
      else setConfirmPasswordValidationErrorMessage('')
      setConfirmNewPassword(value)
    }

    const handlePasswordUpdate = async () => {
      const newPassword = newPasswordRef.current?.value?.toString()
      const confirmPassword = confirmPasswordRef.current?.value?.toString()
      const phoneNumber = phoneNumberRef.current?.value?.toString()

      const validationmessages = validatePassword(newPassword!)
      if (validationmessages.find((message) => message.color === 'red')) {
        dispatch(showErrorMessage('Please meet login requirements'))
        return
      }
      if (!(newPassword?.length && newPassword?.length > 0)) {
        dispatch(showErrorMessage('New Password is required'))
        return
      }
      if (!(confirmPassword?.length && confirmPassword?.length > 0)) {
        dispatch(showErrorMessage('Confirm Password is required'))
        return
      }
      if (newPassword !== confirmPassword) {
        dispatch(showErrorMessage("Password doesn't matches"))
        return
      }
      if (!(phoneNumber?.length && phoneNumber?.length > 0)) {
        dispatch(showErrorMessage('Phone Number is required'))
        return
      }
      const signinResponse = await confirmSignIn({
        challengeResponse: newPassword || '',
        options: {
          userAttributes: {
            phone_number: phoneNumber,
          },
        },
      })
      await nextStep(signinResponse)
    }

    return (
      <Box
        component='form'
        sx={{
          '& > :not(style)': { m: 1 },
          background: '#06004A',
          color: 'white',
          backgroundImage: "url('/images/login_background.png')",
        }}
        display='flex'
        flexDirection={'column'}
        justifyContent='center'
        alignItems='center'
        alignContent='center'
      >
        <Header></Header>
        <FormControl sx={{ m: 1, minHeight: '45vh' }}>
          <FormLabel
            sx={{
              color: 'white',
              fontSize: '10px',
              margin: '3px',
              marginLeft: '15px',
            }}
          >
            New Password
          </FormLabel>
          <TextField
            inputRef={newPasswordRef}
            placeholder='Enter new password'
            type='password'
            value={newPassword}
            onChange={(e) => handlePasswordChange(e.target.value)}
            sx={{
              background: 'white',
              margin: '3px',
              width: '350px',
              opacity: '0px',
              borderRadius: '4px',
              padding: '8px, 8px, 8px, 16px',
            }}
            size='small'
          />
          <div
            style={{
              paddingLeft: '15px',
              marginTop: '3px',
              marginBottom: '3px',
            }}
          >
            <div style={{ fontSize: '10px' }}>Password Requirements</div>
            {passwordValidationErrorMessage?.map((message: any) => {
              return (
                <div style={{ display: 'flex', gap: '2px' }}>
                  <img
                    src={
                      message.color === 'red'
                        ? '/images/warning.png'
                        : '/images/success.png'
                    }
                  />
                  <div style={{ color: message.color, fontSize: '12px' }}>
                    {message.description}
                  </div>
                </div>
              )
            })}
          </div>
          <FormLabel
            style={{
              color: 'white',
              margin: '3px',
              marginLeft: '15px',
              fontSize: '10px',
            }}
          >
            Confirm Password
          </FormLabel>
          <TextField
            placeholder='Enter confirm password'
            inputRef={confirmPasswordRef}
            type='password'
            value={confirmNewPassword}
            sx={{
              background: 'white',
              margin: '3px',
              width: '350px',
              opacity: '0px',
              borderRadius: '4px',
            }}
            size='small'
            onChange={(e) => handleConfirmPasswordChange(e.target.value)}
          />
          <div style={{ color: 'red', paddingLeft: '15px' }}>
            {confirmPasswordValidationErrorMessage}
          </div>
          <FormLabel
            style={{
              color: 'white',
              margin: '3px',
              marginLeft: '15px',
              fontSize: '10px',
            }}
          >
            Phone Number
          </FormLabel>
          <TextField
            placeholder='+1215551234'
            inputRef={phoneNumberRef}
            required
            value={phoneNumber}
            sx={{
              background: 'white',
              margin: '3px',
              width: '350px',
              opacity: '0px',
              borderRadius: '4px',
            }}
            size='small'
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
          <Button
            onClick={handlePasswordUpdate}
            sx={{ color: 'white', margin: '15px' }}
          >
            Sign in
          </Button>
        </FormControl>
        <Footer></Footer>
      </Box>
    )
  }

  const MFAForm = () => {
    return (
      <Box
        component='form'
        sx={{
          '& > :not(style)': { m: 1 },
          background: '#06004A',
          color: 'white',
          backgroundImage: "url('/images/login_background.png')",
        }}
        display='flex'
        flexDirection={'column'}
        justifyContent='center'
        alignItems='center'
        alignContent='center'
      >
        <Header></Header>
        <FormControl
          sx={{
            m: 1,
            minHeight: '45vh',
          }}
        >
          <FormLabel
            sx={{
              color: 'white',
              marginBottom: '15px',
              textAlign: 'center',
            }}
          >
            2 Step Verification
          </FormLabel>
          <FormLabel
            sx={{
              color: 'white',
              fontSize: '10px',
              marginLeft: '15px',
              padding: '0px, 16px, 0px, 16px',
              margin: '3px',
            }}
          >
            Passcode
          </FormLabel>
          <TextField
            placeholder='Enter passcode'
            inputRef={mfaCodeRef}
            sx={{
              background: 'white',
              width: '350px',
              opacity: '0px',
              borderRadius: '4px',
            }}
            size='small'
          />
          <Button onClick={handleMFA} sx={{ color: 'white', margin: '15px' }}>
            Authenticate
          </Button>
        </FormControl>
        <Footer></Footer>
      </Box>
    )
  }

  const ForgotPasswordForm = () => {
    return (
      <Box
        component='form'
        sx={{
          '& > :not(style)': { m: 1 },
          background: '#06004A',
          color: 'white',
          backgroundImage: "url('/images/login_background.png')",
        }}
        display='flex'
        flexDirection={'column'}
        justifyContent='center'
        alignItems='center'
        alignContent='center'
      >
        <Header></Header>
        <FormControl
          sx={{
            m: 1,
            minHeight: '45vh',
          }}
        >
          <FormLabel
            sx={{
              color: 'white',
              fontSize: '18px',
              marginLeft: '15px',
              padding: '0px, 16px, 0px, 16px',
              margin: '3px',
            }}
          >
            Forgot your Password?
          </FormLabel>
          <FormLabel
            sx={{
              color: 'white',
              fontSize: '10px',
              marginLeft: '15px',
              padding: '0px, 16px, 0px, 16px',
              margin: '3px',
            }}
          >
            Username
          </FormLabel>
          <TextField
            placeholder='Enter username'
            inputRef={forgotPasswordUsernameRef}
            sx={{
              background: 'white',
              width: '350px',
              opacity: '0px',
              borderRadius: '4px',
            }}
            size='small'
          />
          <Button
            onClick={() => handleResetPassword()}
            sx={{ color: 'white', margin: '15px' }}
          >
            Reset my password
          </Button>
        </FormControl>
        <Footer></Footer>
      </Box>
    )
  }

  const ConfirmResetPasswordForm = () => {
    const [newPassword, setNewPassword] = useState<string>()
    const [confirmNewPassword, setConfirmNewPassword] = useState<string>()
    const [passwordValidationErrorMessage, setPasswordValidationErrorMessage] =
      useState<any>(Object.values(errors))
    const [
      confirmPasswordValidationErrorMessage,
      setConfirmPasswordValidationErrorMessage,
    ] = useState<string>('')

    const handlePasswordChange = (value: string) => {
      setNewPassword(value)
      setPasswordValidationErrorMessage(validatePassword(value))
    }

    const handleConfirmPasswordChange = (value: string) => {
      if (newPassword !== value)
        setConfirmPasswordValidationErrorMessage("Password doesn't matches")
      else setConfirmPasswordValidationErrorMessage('')
      setConfirmNewPassword(value)
    }

    const handleResetPasswordUpdate = async () => {
      const newPassword = newPasswordRef.current?.value?.toString()
      const confirmPassword = confirmPasswordRef.current?.value?.toString()
      const code = resetPasswordCodeRef.current?.value?.toString()

      const validationmessages = validatePassword(newPassword!)
      if (validationmessages.find((message) => message.color === 'red')) {
        dispatch(showErrorMessage('Please meet login requirements'))
        return
      }

      if (!(newPassword?.length && newPassword?.length > 0)) {
        dispatch(showErrorMessage('New Password is required'))
        return
      }
      if (!(confirmPassword?.length && confirmPassword?.length > 0)) {
        dispatch(showErrorMessage('Confirm Password is required'))
        return
      }
      if (!(code?.length && code?.length > 0)) {
        dispatch(showErrorMessage('Code is required'))
        return
      }
      if (newPassword !== confirmPassword) {
        dispatch(showErrorMessage("Password doesn't matches"))
        return
      }
      await confirmResetPassword({
        username: userName!,
        newPassword: newPassword,
        confirmationCode: code,
      })
      setConfirmForgotPassword(false)
      setLogin(true)
    }

    return (
      <Box
        component='form'
        sx={{
          '& > :not(style)': { m: 1 },
          background: '#06004A',
          color: 'white',
          backgroundImage: "url('/images/login_background.png')",
        }}
        display='flex'
        flexDirection={'column'}
        justifyContent='center'
        alignItems='center'
        alignContent='center'
      >
        <Header></Header>
        <FormControl sx={{ m: 1, minHeight: '45vh' }}>
          <FormLabel
            sx={{
              color: 'white',
              fontSize: '10px',
              margin: '3px',
              marginLeft: '15px',
            }}
          >
            Code
          </FormLabel>
          <TextField
            inputRef={resetPasswordCodeRef}
            placeholder='Enter code'
            sx={{
              background: 'white',
              margin: '3px',
              width: '350px',
              opacity: '0px',
              borderRadius: '4px',
              padding: '8px, 8px, 8px, 16px',
            }}
            size='small'
          />
          <FormLabel
            sx={{
              color: 'white',
              fontSize: '10px',
              margin: '3px',
              marginLeft: '15px',
            }}
          >
            New Password
          </FormLabel>
          <TextField
            inputRef={newPasswordRef}
            placeholder='Enter new password'
            type='password'
            value={newPassword}
            sx={{
              background: 'white',
              margin: '3px',
              width: '350px',
              opacity: '0px',
              borderRadius: '4px',
              padding: '8px, 8px, 8px, 16px',
            }}
            onChange={(e) => handlePasswordChange(e.target.value)}
            size='small'
          />
          <div
            style={{
              paddingLeft: '15px',
              marginTop: '3px',
              marginBottom: '3px',
            }}
          >
            <div style={{ fontSize: '10px' }}>Password Requirements</div>
            {passwordValidationErrorMessage?.map((message: any) => {
              return (
                <div style={{ display: 'flex', gap: '2px' }}>
                  <img
                    src={
                      message.color === 'red'
                        ? '/images/warning.png'
                        : '/images/success.png'
                    }
                  />
                  <div style={{ color: message.color, fontSize: '12px' }}>
                    {message.description}
                  </div>
                </div>
              )
            })}
          </div>
          <FormLabel
            style={{
              color: 'white',
              margin: '3px',
              marginLeft: '15px',
              fontSize: '10px',
            }}
          >
            Confirm Password
          </FormLabel>
          <TextField
            placeholder='Enter confirm password'
            inputRef={confirmPasswordRef}
            type='password'
            value={confirmNewPassword}
            sx={{
              background: 'white',
              margin: '3px',
              width: '350px',
              opacity: '0px',
              borderRadius: '4px',
            }}
            size='small'
            onChange={(e) => handleConfirmPasswordChange(e.target.value)}
          />
          <div style={{ color: 'red', paddingLeft: '15px' }}>
            {confirmPasswordValidationErrorMessage}
          </div>
          <Button
            onClick={handleResetPasswordUpdate}
            sx={{ color: 'white', margin: '15px' }}
          >
            Change Password
          </Button>
        </FormControl>
        <Footer></Footer>
      </Box>
    )
  }

  if (mfa) return <MFAForm />
  else if (confirmPassword) return <ConfirmPasswordForm />
  else if (forgotPassword) return <ForgotPasswordForm />
  else if (confirmForgotPassword) return <ConfirmResetPasswordForm />
  else if (login) return <LoginForm />
  else return <LoginForm />
}

export default Authenticator
